import {captureException} from '@sentry/browser';
import {Mutex} from 'async-mutex';
import {openDB, IDBPDatabase} from 'idb';

import {ApiCacheDB} from './types/ApiCacheDB';
import {deviceErrorUtils} from './utils/deviceErrorUtils';

// `idb` queued requests return promises that reject independently when a transaction
// aborts (tab close, mid-flight navigation, mutex-induced idle). `tx.done` is our source
// of truth for transaction outcome, so per-request rejections are redundant — suppress
// them to avoid `unhandledrejection` noise reaching the global error reporter.
const suppress = <T>(request: Promise<T>): Promise<T> => {
  request.catch(console.error);
  return request;
};

const DB_NAME = 'omnetic-rtk-cache';
const STORE_NAME = 'apiCache';
const DB_VERSION = 2;

const mutex = new Mutex();

// Aim for ~512KB chunks. JS strings are UTF-16 (2 bytes/char approx)
const CHUNK_SIZE_CHARS = 256 * 1024; // 512KB / 2 bytes/char
const CHUNK_KEY_SEPARATOR = '__chunk__';

interface ChunkMetadata {
  isChunked: true;
  numChunks: number;
}

function isChunkMetadata(data: unknown): data is ChunkMetadata {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as Record<string, unknown>).isChunked === true &&
    typeof (data as Record<string, unknown>).numChunks === 'number'
  );
}

/**
 * Create chunks from serialized string
 */
function createChunks(serialized: string): string[] {
  const chunks: string[] = [];
  for (let i = 0; i < serialized.length; i += CHUNK_SIZE_CHARS) {
    chunks.push(serialized.substring(i, i + CHUNK_SIZE_CHARS));
  }
  return chunks;
}

// Database connection singleton
let dbPromise: Promise<IDBPDatabase<ApiCacheDB>> | null = null;

const isIndexedDBSupported = () => {
  try {
    return 'indexedDB' in window && window.indexedDB !== null;
  } catch {
    return false;
  }
};

/**
 * Get or initialize the IndexedDB database connection
 */
const getDB = () => {
  if (!isIndexedDBSupported()) {
    console.error('IndexedDB is not supported in this browser');
    return Promise.reject();
  }
  if (!dbPromise) {
    dbPromise = openDB<ApiCacheDB>(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion, newVersion, transaction) {
        // Create store if it doesn't exist (first time)
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        } else if (oldVersion < DB_VERSION) {
          // Clear data only when upgrading from older versions. Suppress the request-level
          // rejection: `clear()` is a range delete that WebKit can fail post-auto-commit,
          // and the version-change transaction outcome is awaited via `openDB` itself.
          const store = transaction.objectStore(STORE_NAME);
          suppress(store.clear());
          console.warn(`Cleared cache data when upgrading from v${oldVersion} to v${newVersion}`);
        }
      },
    });
  }
  return dbPromise;
};

/**
 * Save data to IndexedDB using synchronous queuing pattern to prevent TransactionInactiveError
 */
export const saveToIDB = async (key: string, value: unknown): Promise<void> => {
  await mutex.runExclusive(async () => {
    try {
      const db = await getDB();

      // Complete ALL async work BEFORE starting transaction
      let serialized: string;
      let chunks: string[] | null = null;
      let metadata: ChunkMetadata | null = null;

      try {
        serialized = JSON.stringify(value);
      } catch (serializeError) {
        console.warn(
          `Could not serialize value for key "${key}". Saving directly.`,
          serializeError
        );
        // For non-serializable data, save directly without chunking
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        suppress(store.delete(key)); // Clean up any previous data
        suppress(store.put(value, key));
        await tx.done;
        return;
      }

      // Check if chunking is needed and prepare chunks
      if (serialized.length > CHUNK_SIZE_CHARS) {
        chunks = createChunks(serialized);
        metadata = {
          isChunked: true,
          numChunks: chunks.length,
        };
      }

      // Read existing data to know what to clean up (separate transaction)
      const readTx = db.transaction(STORE_NAME, 'readonly');
      const readStore = readTx.objectStore(STORE_NAME);
      const existingData = await readStore.get(key);
      await readTx.done;

      // Write transaction with ALL operations queued synchronously
      const writeTx = db.transaction(STORE_NAME, 'readwrite');
      const writeStore = writeTx.objectStore(STORE_NAME);

      // Clean up old chunks if any (all synchronous operations)
      if (isChunkMetadata(existingData)) {
        for (let i = 0; i < existingData.numChunks; i++) {
          suppress(writeStore.delete(`${key}${CHUNK_KEY_SEPARATOR}${i}`));
        }
      }
      suppress(writeStore.delete(key)); // Always delete main key

      // Save new data (all synchronous operations)
      if (chunks && metadata) {
        // Save chunked data
        suppress(writeStore.put(metadata, key));
        chunks.forEach((chunk, i) => {
          suppress(writeStore.put(chunk, `${key}${CHUNK_KEY_SEPARATOR}${i}`));
        });
      } else {
        // Save non-chunked data
        suppress(writeStore.put(value, key));
      }

      await writeTx.done;
    } catch (error) {
      const payloadSize = deviceErrorUtils.calculatePayloadSizeInBytes(value);
      const errorInfo = deviceErrorUtils.analyzeDeviceError(error, payloadSize);

      console.error(`[IndexedDB] Save failed for key "${key}":`, error);

      if (errorInfo.isDeviceError) {
        console.warn(`[IndexedDB] Device limitation detected: ${errorInfo.category}`);
        if (errorInfo.details.detectionReason) {
          console.warn(`[IndexedDB] Reason: ${errorInfo.details.detectionReason}`);
        }
      }

      // Only log to Sentry if it's an actionable application error
      if (errorInfo.shouldLogToSentry) {
        captureException(error, {
          contexts: {
            indexedDB: {
              operation: 'save',
              key,
              errorCategory: errorInfo.category,
              ...errorInfo.details,
            },
          },
        });
      }
    }
  });
};

/**
 * Load data from IndexedDB, reassembling chunks if necessary
 */
export const loadFromIDB = async (key: string): Promise<unknown | null> =>
  await mutex.runExclusive(async () => {
    try {
      const db = await getDB();
      const data = await db.get(STORE_NAME, key);

      if (isChunkMetadata(data)) {
        // It's chunked data, retrieve and reassemble
        const chunkKeys = Array.from(
          {length: data.numChunks},
          (_, i) => `${key}${CHUNK_KEY_SEPARATOR}${i}`
        );

        const tx = db.transaction(STORE_NAME, 'readonly');
        const store = tx.objectStore(STORE_NAME);
        const chunks = await Promise.all(chunkKeys.map((chunkKey) => store.get(chunkKey)));
        await tx.done;

        // Check if any chunk is missing (shouldn't happen ideally)
        if (chunks.some((chunk) => typeof chunk !== 'string')) {
          // Clean up corrupted chunked data
          const deleteTx = db.transaction(STORE_NAME, 'readwrite');
          const deleteStore = deleteTx.objectStore(STORE_NAME);

          // Delete all chunks and metadata synchronously
          for (let i = 0; i < data.numChunks; i++) {
            suppress(deleteStore.delete(`${key}${CHUNK_KEY_SEPARATOR}${i}`));
          }
          suppress(deleteStore.delete(key));

          await deleteTx.done;
          return null;
        }

        const serializedData = (chunks as string[]).join('');
        try {
          return JSON.parse(serializedData);
        } catch (parseError) {
          const errorInfo = deviceErrorUtils.analyzeDeviceError(parseError);

          // Always log to console for debugging
          console.error(`[IndexedDB] Parse error for key "${key}":`, parseError);

          if (errorInfo.isDeviceError) {
            console.warn(`[IndexedDB] Device limitation detected: ${errorInfo.category}`);
          }

          // Only log to Sentry if it's an actionable application error
          if (errorInfo.shouldLogToSentry) {
            captureException(parseError, {
              contexts: {
                indexedDB: {
                  operation: 'load-parse',
                  key,
                  errorCategory: errorInfo.category,
                  ...errorInfo.details,
                },
              },
            });
          }
          return null;
        }
      } else {
        // It's not chunked data or doesn't exist
        return data || null;
      }
    } catch (error) {
      const errorInfo = deviceErrorUtils.analyzeDeviceError(error);

      // Always log to console for debugging
      console.error(`[IndexedDB] Load failed for key "${key}":`, error);

      if (errorInfo.isDeviceError) {
        console.warn(`[IndexedDB] Device limitation detected: ${errorInfo.category}`);
        if (errorInfo.details.detectionReason) {
          console.warn(`[IndexedDB] Reason: ${errorInfo.details.detectionReason}`);
        }
      }

      // Only log to Sentry if it's an actionable application error
      if (errorInfo.shouldLogToSentry) {
        captureException(error, {
          contexts: {
            indexedDB: {
              operation: 'load',
              key,
              errorCategory: errorInfo.category,
              ...errorInfo.details,
            },
          },
        });
      }
      return null;
    }
  });

/**
 * Delete data from IndexedDB, handling chunks if necessary
 */
export const deleteFromIDB = async (key: string): Promise<void> => {
  await mutex.runExclusive(async () => {
    try {
      const db = await getDB();

      // Read existing data to know what to clean up (separate transaction)
      let existingData: unknown;
      {
        const readTx = db.transaction(STORE_NAME, 'readonly');
        existingData = await readTx.objectStore(STORE_NAME).get(key);
        await readTx.done;
      }

      // Delete transaction with all operations queued synchronously
      const deleteTx = db.transaction(STORE_NAME, 'readwrite');
      const deleteStore = deleteTx.objectStore(STORE_NAME);

      // Clean up chunks if any (all synchronous operations)
      if (isChunkMetadata(existingData)) {
        for (let i = 0; i < existingData.numChunks; i++) {
          suppress(deleteStore.delete(`${key}${CHUNK_KEY_SEPARATOR}${i}`));
        }
      }
      suppress(deleteStore.delete(key));

      await deleteTx.done;
    } catch (error) {
      const errorInfo = deviceErrorUtils.analyzeDeviceError(error);

      console.error(`[IndexedDB] Delete failed for key "${key}":`, error);

      if (errorInfo.isDeviceError) {
        console.warn(`[IndexedDB] Device limitation detected: ${errorInfo.category}`);
        if (errorInfo.details.detectionReason) {
          console.warn(`[IndexedDB] Reason: ${errorInfo.details.detectionReason}`);
        }
      }

      // Only log to Sentry if it's an actionable application error
      if (errorInfo.shouldLogToSentry) {
        captureException(error, {
          contexts: {
            indexedDB: {
              operation: 'delete',
              key,
              errorCategory: errorInfo.category,
              ...errorInfo.details,
            },
          },
        });
      }
    }
  });
};

/**
 * Clear all data from IndexedDB
 */
export const purgeCache = async (): Promise<void> => {
  await mutex.runExclusive(async () => {
    try {
      const db = await getDB();
      await db.clear(STORE_NAME);
    } catch (error) {
      const errorInfo = deviceErrorUtils.analyzeDeviceError(error);

      console.error('[IndexedDB] Cache purge failed:', error);

      if (errorInfo.isDeviceError) {
        console.warn(`[IndexedDB] Device limitation detected: ${errorInfo.category}`);
        if (errorInfo.details.detectionReason) {
          console.warn(`[IndexedDB] Reason: ${errorInfo.details.detectionReason}`);
        }
      }

      // Only log to Sentry if it's an actionable application error
      if (errorInfo.shouldLogToSentry) {
        captureException(error, {
          contexts: {
            indexedDB: {
              operation: 'clear',
              errorCategory: errorInfo.category,
              ...errorInfo.details,
            },
          },
        });
      }
    }
  });
};

export const storage = {
  saveToIDB,
  loadFromIDB,
  deleteFromIDB,
  purgeCache,
};
